import { useState } from 'react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { formatUnits, parseUnits } from 'viem';

/**
 * Staking Modal Component
 * Handles stake/unstake operations with amount input
 * Extracted from StakingPoolCard.js for better code organization
 */
const StakingModal = ({
  isOpen,
  onClose,
  mode, // 'stake' or 'unstake'
  tokenSymbol,
  tokenDecimals,
  balance, // Available balance (tokenBalance for stake, userStaked for unstake)
  allowance, // Token allowance for staking contract
  isApproving,
  isProcessing,
  onApprove,
  onStake,
  onUnstake,
}) => {
  const [amount, setAmount] = useState('');

  if (!isOpen) return null;
  
  // Calculate if approval is needed based on entered amount
  let needsApproval = false;
  try {
    if (amount && parseFloat(amount) > 0) {
      const amountToParse = parseUnits(amount.toString(), tokenDecimals);
      needsApproval = (allowance || 0n) < amountToParse;
    }
  } catch (e) {
    // Invalid amount format, no approval check needed
    needsApproval = false;
  }

  const formattedBalance = parseFloat(
    formatUnits(balance || 0n, tokenDecimals)
  ).toLocaleString(undefined, {
    minimumFractionDigits: tokenSymbol.includes('LP') ? 6 : 0,
    maximumFractionDigits: tokenSymbol.includes('LP') ? 8 : 2,
  });

  const handlePercentClick = (percent) => {
    if (!balance) return;
    const balanceFloat = parseFloat(formatUnits(balance, tokenDecimals));
    const selectedAmount = (balanceFloat * percent) / 100;
    setAmount(selectedAmount.toString());
  };

  const handleClose = () => {
    setAmount('');
    onClose();
  };

  const handleAction = async () => {
    if (mode === 'unstake') {
      const success = await onUnstake(amount);
      if (success) handleClose();
    } else if (needsApproval) {
      await onApprove();
    } else {
      const success = await onStake(amount);
      if (success) handleClose();
    }
  };

  const isDisabled = isProcessing || isApproving || !amount || parseFloat(amount) <= 0;

  return createPortal(
    <motion.div
      className="staking-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={handleClose}
    >
      <div className="staking-modal" onClick={(e) => e.stopPropagation()}>
        <div className="staking-modal-header">
          <h3>
            {mode === 'unstake' ? 'Unstake' : 'Stake'} {tokenSymbol}
          </h3>
          <button className="staking-modal-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        <div className="staking-modal-body">
          {/* Approval Warning */}
          {mode === 'stake' && needsApproval && (
            <div style={{
              background: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              color: '#ffc107'
            }}>
              <strong>⚠️ Approval Required</strong>
              <p style={{ margin: '8px 0 0', fontSize: '14px', opacity: 0.9 }}>
                You need to approve the staking contract to spend your tokens. 
                Click "Approve" button below, then you can stake.
              </p>
            </div>
          )}

          <div className="staking-input-group">
            <div className="staking-input-header">
              <span>Amount</span>
              <span className="staking-input-balance">
                Balance: {formattedBalance}
              </span>
            </div>
            <input
              type="text"
              className="staking-input"
              placeholder="0.0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          <div className="staking-percent-buttons">
            <button onClick={() => handlePercentClick(25)}>25%</button>
            <button onClick={() => handlePercentClick(50)}>50%</button>
            <button onClick={() => handlePercentClick(75)}>75%</button>
            <button onClick={() => handlePercentClick(100)}>MAX</button>
          </div>

          <div className="staking-modal-actions">
            <motion.button
              className="staking-btn staking-btn-secondary"
              onClick={handleClose}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </motion.button>

            {mode === 'unstake' ? (
              <motion.button
                className="staking-btn staking-btn-primary"
                onClick={handleAction}
                disabled={isDisabled}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isProcessing ? 'Processing...' : 'Unstake'}
              </motion.button>
            ) : needsApproval ? (
              <motion.button
                className="staking-btn staking-btn-primary"
                onClick={handleAction}
                disabled={isDisabled}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isApproving ? 'Approving...' : 'Approve'}
              </motion.button>
            ) : (
              <motion.button
                className="staking-btn staking-btn-primary"
                onClick={handleAction}
                disabled={isDisabled}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isProcessing ? 'Processing...' : 'Stake'}
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </motion.div>,
    document.body
  );
};

export default StakingModal;
